import {formDataToJson} from "./http.js";
import {evaluateLogicCondition} from "./logic.js";

export const defaultSelector = '[data-condition], [data-disabled], a, button, input, select, textarea, .form-logic';
export const defaultDisabledClass = 'd-none';

const getFormState = function (form) {
    return formDataToJson(new FormData(form));
};

const evaluateFieldLogic = function (condition, context) {
    if (condition === '' || condition === false || condition === 'false' || condition === '0' || condition === 0) {
        return false;
    }
    if (condition === true || condition === 'true' || condition === '1' || condition === 1) {
        return true;
    }
    if (typeof condition === 'string') {
        return evaluateLogicCondition(condition, context);
    }
    return false;
};

const templateRegex = /@VAR\(\s*([\w]+?)\s*\)/g;
const textNodeTemplate = Symbol();
const Target = {
    TEXT_CONTENT: Symbol(),
    ATTRIBUTE: Symbol(),
};

const getTextVariables = (text) => {
    let variables = {};
    let hasVariables = false;
    for (let variable of text.matchAll(templateRegex)) {
        variables[variable[0]] = variable[1];
        hasVariables = true;
    }
    return hasVariables ? variables : null;
};

const initTextTemplates = (node) => {
    let variables = {};

    // Text node
    if (node.nodeType === 3) {
        if (!node[textNodeTemplate]) {
            variables = getTextVariables(node.textContent);
            if (variables) {
                node[textNodeTemplate] = [{
                    target: Target.TEXT_CONTENT,
                    template: node.textContent,
                    variables,
                }];
            }
        }
    } else {
        // Attributes
        if (!node[textNodeTemplate]) {
            node[textNodeTemplate] = [];
            if (node.hasAttributes && node.hasAttributes()) {
                for (let attr of node.attributes) {
                    variables = getTextVariables(attr.value);
                    if (variables) {
                        node[textNodeTemplate].push({
                            target: Target.ATTRIBUTE,
                            name: attr.name,
                            template: attr.value,
                            variables,
                        });
                    }
                }
            }
        }
    }
};

const updateTextLabels = (form, context) => {
    const walker = document.createTreeWalker(form, NodeFilter.SHOW_ALL)
    while(walker.nextNode()) {
        if (walker.currentNode[textNodeTemplate] === undefined) {
            initTextTemplates(walker.currentNode);
        }
        if (walker.currentNode[textNodeTemplate]) {
            let text;
            let variableName;
            for (let template of walker.currentNode[textNodeTemplate]) {
                text = template.template;
                for (variableName in template.variables) {
                    text = text.replaceAll(variableName, context[template.variables[variableName]] || '');
                }
                if (template.target === Target.TEXT_CONTENT) {
                    walker.currentNode.textContent = text;
                } else if (template.target === Target.ATTRIBUTE) {
                    walker.currentNode[template.name] = text;
                }
            }
        }
    }
};

/**
 * @typedef {object} EvaluateFormLogicOptions
 * @property {string | undefined} disabledClass
 * @property {string | undefined} selector
 * @property {object | undefined} properties
 */

/**
 * @param {HTMLFontElement} form
 * @param {EvaluateFormLogicOptions} options
 */
export const evaluateFormLogic = (form, options = {}) => {
    const context = {
        ...options.properties,
        ...getFormState(form),
    };
    const elements = form.querySelectorAll(options.selector || defaultSelector);

    let isVisible;
    let isDisabled;
    for (let el of elements) {
        isVisible = true;

        if (el.dataset.condition !== undefined && el.dataset.condition !== '') {
            isVisible = evaluateFieldLogic(el.dataset.condition, context);
        }
        el.classList.toggle(options.disabledClass || defaultDisabledClass, !isVisible);
        isDisabled = !isVisible;
        if (!isDisabled && el.dataset.disabled !== undefined && el.dataset.disabled !== '') {
            isDisabled = evaluateFieldLogic(el.dataset.disabled, context);
        }
        el.disabled = isDisabled;
        if (el.tagName === 'OPTION') {
            el.hidden = isDisabled;
        }

        if (isDisabled || !isVisible) {
            if (el.selected) {
                el.selected = false;
            }
            if (el.checked) {
                el.selected = false;
            }
        }
    }

    updateTextLabels(form, context);
};

